// Frozen network registry — all networks that host accepted coins/tokens
// Networks with isActive=false are kept for future integration but excluded from UI and payment flows

import type { NetworkId, Network } from '../types/network.ts';

// ENSIP-11: ENS coinType for a non-Ethereum EVM chain. Ethereum keeps SLIP-44 60 (the EVM base record).
const _ensip11CoinType = (chainId: number): number => 0x80000000 + chainId;

export const NETWORKS: Readonly<Record<NetworkId, Network>> = Object.freeze({
  bitcoin: Object.freeze({
    id: 'bitcoin' as const,
    name: 'Bitcoin',
    chainFamily: 'utxo' as const,
    chainId: 0,
    coinType: 0,
    nativeDecimals: 8,
    nativeSymbol: 'BTC',
    explorer: Object.freeze({
      baseUrl: 'https://mempool.space',
      txPath: '/tx/',
      addressPath: '/address/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_BTC_RPC',
    iconPath: '/assets/currency-icons/btc.svg',
    isActive: true,
    allowSend: true,
  }),
  litecoin: Object.freeze({
    id: 'litecoin' as const,
    name: 'Litecoin',
    chainFamily: 'utxo' as const,
    chainId: 0,
    coinType: 2,
    nativeDecimals: 8,
    nativeSymbol: 'LTC',
    explorer: Object.freeze({
      baseUrl: 'https://blockchair.com/litecoin',
      txPath: '/transaction/',
      addressPath: '/address/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_LTC_RPC',
    iconPath: '/assets/currency-icons/ltc.svg',
    isActive: true,
    allowSend: true,
  }),
  ethereum: Object.freeze({
    id: 'ethereum' as const,
    name: 'Ethereum',
    chainFamily: 'evm' as const,
    chainId: 1,
    coinType: 60,
    nativeDecimals: 18,
    nativeSymbol: 'ETH',
    explorer: Object.freeze({
      baseUrl: 'https://etherscan.io',
      txPath: '/tx/',
      addressPath: '/address/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_ETHEREUM_RPC',
    iconPath: '/assets/currency-icons/eth.svg',
    isActive: true,
    allowSend: true,
  }),
  polygon: Object.freeze({
    id: 'polygon' as const,
    name: 'Polygon',
    chainFamily: 'evm' as const,
    chainId: 137,
    coinType: _ensip11CoinType(137),
    nativeDecimals: 18,
    nativeSymbol: 'POL',
    explorer: Object.freeze({
      baseUrl: 'https://polygonscan.com',
      txPath: '/tx/',
      addressPath: '/address/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_POLYGON_RPC',
    iconPath: '/assets/currency-icons/pol.svg',
    isActive: true,
    allowSend: true,
  }),
  arbitrum: Object.freeze({
    id: 'arbitrum' as const,
    name: 'Arbitrum',
    chainFamily: 'evm' as const,
    chainId: 42161,
    coinType: _ensip11CoinType(42161),
    nativeDecimals: 18,
    nativeSymbol: 'ETH',
    explorer: Object.freeze({
      baseUrl: 'https://arbiscan.io',
      txPath: '/tx/',
      addressPath: '/address/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_ARBITRUM_RPC',
    iconPath: '/assets/currency-icons/arb.svg',
    isActive: true,
    allowSend: true,
  }),
  optimism: Object.freeze({
    id: 'optimism' as const,
    name: 'Optimism',
    chainFamily: 'evm' as const,
    chainId: 10,
    coinType: _ensip11CoinType(10),
    nativeDecimals: 18,
    nativeSymbol: 'ETH',
    explorer: Object.freeze({
      baseUrl: 'https://optimistic.etherscan.io',
      txPath: '/tx/',
      addressPath: '/address/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_OPTIMISM_RPC',
    iconPath: '/assets/currency-icons/op.svg',
    isActive: true,
    allowSend: true,
  }),
  base: Object.freeze({
    id: 'base' as const,
    name: 'Base',
    chainFamily: 'evm' as const,
    chainId: 8453,
    coinType: _ensip11CoinType(8453),
    nativeDecimals: 18,
    nativeSymbol: 'ETH',
    explorer: Object.freeze({
      baseUrl: 'https://basescan.org',
      txPath: '/tx/',
      addressPath: '/address/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_BASE_RPC',
    iconPath: '/assets/currency-icons/base.svg',
    isActive: true,
    allowSend: true,
  }),
  bsc: Object.freeze({
    id: 'bsc' as const,
    name: 'BNB Chain',
    chainFamily: 'evm' as const,
    chainId: 56,
    coinType: _ensip11CoinType(56),
    nativeDecimals: 18,
    nativeSymbol: 'BNB',
    explorer: Object.freeze({
      baseUrl: 'https://bscscan.com',
      txPath: '/tx/',
      addressPath: '/address/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_BSC_RPC',
    iconPath: '/assets/currency-icons/bnb.svg',
    isActive: true,
    allowSend: true,
  }),
  avalanche: Object.freeze({
    id: 'avalanche' as const,
    name: 'Avalanche',
    chainFamily: 'evm' as const,
    chainId: 43114,
    coinType: _ensip11CoinType(43114),
    nativeDecimals: 18,
    nativeSymbol: 'AVAX',
    explorer: Object.freeze({
      baseUrl: 'https://snowtrace.io',
      txPath: '/tx/',
      addressPath: '/address/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_AVALANCHE_RPC',
    iconPath: '/assets/currency-icons/avax.svg',
    isActive: true,
    allowSend: true,
  }),
  // HyperEVM (Hyperliquid's EVM layer — NOT HyperCore, which uses signed API actions).
  // HYPE is gas only; accepted payment coins are USDT0 + Circle USDC.
  // Live 2026-10-24: browser RPC = Alchemy (VITE_PUBLIC_HYPEREVM_RPC), server = official RPC (100 req/min/IP); ChainSaleFee deployed.
  hyperevm: Object.freeze({
    id: 'hyperevm' as const,
    name: 'HyperEVM',
    chainFamily: 'evm' as const,
    chainId: 999,
    coinType: _ensip11CoinType(999),
    nativeDecimals: 18,
    nativeSymbol: 'HYPE',
    explorer: Object.freeze({
      baseUrl: 'https://hyperevmscan.io',
      txPath: '/tx/',
      addressPath: '/address/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_HYPEREVM_RPC',
    iconPath: '/assets/currency-icons/hyp.svg',
    isActive: true,
    allowSend: true,
  }),
  solana: Object.freeze({
    id: 'solana' as const,
    name: 'Solana',
    chainFamily: 'solana' as const,
    chainId: 0,
    coinType: 501,
    nativeDecimals: 9,
    nativeSymbol: 'SOL',
    explorer: Object.freeze({
      baseUrl: 'https://solscan.io',
      txPath: '/tx/',
      addressPath: '/account/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_SOLANA_RPC',
    iconPath: '/assets/currency-icons/sol.svg',
    isActive: true,
    allowSend: true,
  }),
  tron: Object.freeze({
    id: 'tron' as const,
    name: 'Tron',
    chainFamily: 'tron' as const,
    chainId: 728126428,
    coinType: 195,
    nativeDecimals: 6,
    nativeSymbol: 'TRX',
    explorer: Object.freeze({
      baseUrl: 'https://tronscan.org',
      txPath: '/#/transaction/',
      addressPath: '/#/address/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_TRON_RPC',
    iconPath: '/assets/currency-icons/trx.svg',
    isActive: true,
    allowSend: true,
  }),
  xrp: Object.freeze({
    id: 'xrp' as const,
    name: 'XRP Ledger',
    chainFamily: 'xrp' as const,
    chainId: 0,
    coinType: 144,
    nativeDecimals: 6,
    nativeSymbol: 'XRP',
    explorer: Object.freeze({
      baseUrl: 'https://xrpscan.com',
      txPath: '/tx/',
      addressPath: '/account/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_RIPPLE_RPC',
    iconPath: '/assets/currency-icons/xrp.svg',
    isActive: false,
    allowSend: false,
  }),
  ton: Object.freeze({
    id: 'ton' as const,
    name: 'TON',
    chainFamily: 'ton' as const,
    chainId: 0,
    coinType: 607,
    nativeDecimals: 9,
    nativeSymbol: 'TON',
    explorer: Object.freeze({
      baseUrl: 'https://tonviewer.com',
      txPath: '/transaction/',
      addressPath: '/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_TON_RPC',
    iconPath: '/assets/currency-icons/ton.svg',
    isActive: true,
    allowSend: true,
  }),
  // Sui: inactive for v1 — sui:pay standard exists but wallet support is sparse (only Vera),
  // Reown supports Sui via UniversalConnector but no major wallets confirmed WalletConnect compat.
  // Re-evaluate when Slush/Suiet implement sui:pay and Reown adapter matures.
  sui: Object.freeze({
    id: 'sui' as const,
    name: 'Sui',
    chainFamily: 'sui' as const,
    chainId: 0,
    coinType: 784,
    nativeDecimals: 9,
    nativeSymbol: 'SUI',
    explorer: Object.freeze({
      baseUrl: 'https://explorer.sui.network',
      txPath: '/txblock/',
      addressPath: '/address/',
    }),
    publicRpcEnvKey: 'VITE_PUBLIC_SUI_RPC',
    iconPath: '/assets/currency-icons/sui.svg',
    isActive: false,
    allowSend: false,
  }),
});

// Active networks only — use this for UI display, coin selection, and payment flows
export const ACTIVE_NETWORKS: Readonly<Record<string, Network>> = Object.freeze(
  Object.fromEntries(
    Object.entries(NETWORKS).filter(([_, n]) => n.isActive),
  ) as Record<string, Network>,
);

export const ACTIVE_NETWORK_IDS: ReadonlyArray<NetworkId> = Object.freeze(
  Object.keys(ACTIVE_NETWORKS) as NetworkId[],

);

export const isActiveNetwork = (networkId: NetworkId): boolean =>
  NETWORKS[networkId]?.isActive ?? false;

export const isSendAllowed = (networkId: NetworkId): boolean =>
  NETWORKS[networkId]?.allowSend ?? false;

// Chain families with wallet adapter support for in-app sending (Scan + Send)
// allowSend gates the standalone Send page per network; inactive networks (XRP, Sui) stay off
const ADAPTER_CHAIN_FAMILIES: ReadonlySet<string> = new Set(['evm', 'utxo', 'solana', 'tron', 'ton']);

// utxo adapter is sats-connect, which is Bitcoin-only — Litecoin gets WalletConnect + QR, no wallet-app button
const NO_ADAPTER_NETWORKS: ReadonlySet<NetworkId> = new Set<NetworkId>(['litecoin']);

export const hasWalletAdapter = (networkId: NetworkId): boolean => {
  const network = NETWORKS[networkId];
  return network !== undefined && network.isActive && ADAPTER_CHAIN_FAMILIES.has(network.chainFamily)
    && !NO_ADAPTER_NETWORKS.has(networkId);
};
